'use client';

import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { vercelStegaSplit } from '@vercel/stega';
import { mqLargeMin } from 'styled/tools/media-queries';
import ProductBenefitImages from 'components/pdp/product-benefit-images';
import DSProductBenefitImages from 'components/pdp/ds01/ds-product-benefit-images';
import ClinicalTrials from 'components/pdp/clinical-trials';
import Icon from 'components/global/Icon/Primary';
import { trackEvent } from 'lib/analytics';
import { supFootnoteMarkersHtml } from 'lib/utils/footnote-markers';
import { imageUrl, richTextToHtml } from 'components/pdp/modules/helpers';
import type {
  BenefitsModule,
  BenefitsStep,
  RichText,
} from 'components/pdp/modules/types';

const BenefitsWrapper = styled.div<{ $isDS?: boolean }>`
  margin: auto;
  padding: 3.5rem 1rem;
  max-width: 1440px;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ $isDS }) =>
    $isDS &&
    `
    align-items: flex-start;
  `}

  @media ${mqLargeMin} {
    padding: 5rem 1.875rem;

    ${({ $isDS }) =>
      $isDS &&
      `
      align-items: center;
    `}
  }
`;

const BenefitsContents = styled.div`
  margin-top: 2.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 2.5rem;

  @media ${mqLargeMin} {
    margin-top: 3.5rem;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }
`;

/** DS-01: mobile-only Dr. Zain video under CTA; desktop uses full grid in column. */
const DSBenefitVideoMobileSlot = styled.div`
  width: 100%;
  margin-top: 2rem;

  @media ${mqLargeMin} {
    display: none;
  }
`;

const DSDesktopBenefitMediaSlot = styled.div`
  display: none;

  @media ${mqLargeMin} {
    display: block;
  }
`;

const DSMobileBenefitImagesSlot = styled.div`
  width: 100%;

  @media ${mqLargeMin} {
    display: none;
  }
`;

const BenefitTitle = styled.h2<{ $isDS?: boolean }>`
  margin-bottom: 1.5rem;
  font-size: 32px;
  font-weight: 400;
  line-height: 110%;
  letter-spacing: -0.64px;
  text-wrap: unset;
  text-align: center;
  color: var(--text-primary);

  ${({ $isDS }) =>
    $isDS &&
    `
    text-align: left;

    @media ${mqLargeMin} {
      text-align: center;
    }
  `}

  @media ${mqLargeMin} {
    font-size: 48px;
    letter-spacing: -0.96px;
  }
`;

const BenefitIntro = styled.div<{ $isDS?: boolean }>`
  max-width: 40rem;

  ${({ $isDS }) =>
    $isDS &&
    `
    text-align: left;
  `}

  @media ${mqLargeMin} {
    text-align: center;
  }
`;

const ButtonWrapper = styled.ul`
  position: relative;
  padding: 0rem 0.5rem;
  list-style: none;

  @media ${mqLargeMin} {
    padding: 1rem 2rem;
  }
`;

const WeekItem = styled.li`
  position: relative;
  padding-left: 2rem;
  opacity: 0.25;
  transition: opacity 0.2s;

  & + & {
    margin-top: 2.5rem;
  }

  &.selected {
    opacity: 1;
  }

  &:not(:last-of-type)::before {
    content: '';
    position: absolute;
    left: 0;
    top: 13px;
    height: calc(3rem + 100%);
    width: 1px;
    background-color: black;
    opacity: 0.25;
    transition: opacity 0.2s;
  }

  &::after {
    content: '';
    position: absolute;
    left: -3px;
    top: 13px;
    border-radius: 100%;
    height: 7px;
    width: 7px;
    background-color: black;
    opacity: 0.25;
    transition: opacity 0.2s;
  }

  &:has(~ .selected)::before,
  &:has(~ .selected)::after,
  &.selected::after {
    opacity: 1;
  }
`;

const WeekTitle = styled.p`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const WeekPill = styled.span`
  border-radius: 2rem;
  padding: 0 12px;
  height: 1.875rem;
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 0.56px;
  text-wrap: nowrap;
  background-color: var(--fill-brand);
  color: var(--text-inverse-primary);
`;

const WeekBenefits = styled.ul`
  margin-top: 0.5rem;
  padding-left: 1.25rem;
  list-style: disc;

  &.single-benefit {
    padding-left: 0;
    list-style: none;
  }
`;

const HowToUse = styled.div`
  margin: 1rem 0;
  border-radius: 1rem;
  padding: 1rem;
  padding-left: 0;
  display: flex;
  align-items: center;
  background-color: var(--fill-secondary);
  overflow: hidden;

  @media ${mqLargeMin} {
    margin: 2rem 1.5rem 0;
  }
`;

const HowToUseImage = styled.img`
  width: 5.25rem;
  height: 5.25rem;
  object-fit: contain;
`;

const HowToUseList = styled.ul`
  margin-left: 1rem;
  list-style-position: outside;
  font-size: 14px;
  list-style-type: disc;

  @media ${mqLargeMin} {
    font-size: 16px;
  }

  & li {
    font-size: inherit;
  }
`;

const OpenTrialsButton = styled.button`
  appearance: none;
  display: inline-flex;
  margin-top: 1rem;
  border: 0;
  align-items: center;
  gap: 0.5rem;
  text-decoration: underline;
  font-weight: 500;
  color: var(--text-primary);
  background-color: transparent;
  cursor: pointer;
`;

// "1" / "8+" read "Week 1" / "Week 8+"; "Ongoing" is the label itself.
const stepLabel = (week?: string) =>
  /^\d/.test(week ?? '') ? `Week ${week}` : week;

interface ProductBenefitsProps {
  benefits: BenefitsModule;
  isDS?: boolean;
  productInfo?: Record<string, unknown>;
  clinicalTrials?: RichText;
}

const ProductBenefits = ({
  benefits,
  isDS = false,
  productInfo,
  clinicalTrials,
}: ProductBenefitsProps) => {
  if (!benefits) {
    return null;
  }

  const ref = useRef(null);
  const [currentWeek, setCurrentWeek] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [inBrowser, setInBrowser] = useState(false);
  useEffect(() => {
    setInBrowser(true);
  }, []);

  // Non-DS products get a 4th step only when its week is set (e.g. "Ongoing").
  const slotCount = isDS || benefits.steps?.[3]?.week ? 4 : 3;
  const weeks: BenefitsStep[] = Array.from(
    { length: slotCount },
    (_, i) => benefits.steps?.[i] ?? {},
  );

  const pillBackground = benefits.pillColor;
  const benefitImages = benefits.images;
  const howToUseText = benefits.howToUseText;
  if (!howToUseText) {
    return null;
  }
  // In preview the text carries hidden markers for the editor. This pulls them out before
  // splitting on periods so they stay in one piece.
  const { cleaned: howToUse, encoded: howToUseStega } =
    vercelStegaSplit(howToUseText);
  const how_to_use_lines = howToUse.split('.').filter((line) => {
    return line.trim() !== '';
  });

  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) {
        return;
      }
      const refDimensions = ref.current.getBoundingClientRect();
      // calculate the distance of the middle of the list container from the top of the screen as a percent
      const positionFromTopAsPercent =
        (100.0 *
          (window.innerHeight - refDimensions.y - refDimensions.height / 2)) /
        window.innerHeight;
      // get the highlight index based off of how far from the top the container is, and how many list items there are
      const newButtonIndex = Math.floor(
        positionFromTopAsPercent / (100.0 / weeks.length - 1),
      );
      // ensure there's always a valid selected index (set bounds are first/last item indices)
      setCurrentWeek(Math.max(Math.min(newButtonIndex, weeks.length - 1), 0));
    };
    // clean up so we don't end up with multiple listeners for scroll events
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  try {
    return (
      <BenefitsWrapper $isDS={isDS}>
        {inBrowser && (
          <>
            <BenefitTitle $isDS={isDS}>
              {isDS && (
                <>
                  The DS-01<sup>®</sup> difference:
                  <br />
                  Benefits that build over time
                </>
              )}
              {!isDS && 'Benefits that build over time.'}
            </BenefitTitle>
            <BenefitIntro $isDS={isDS}>{benefits.introContent}</BenefitIntro>
            {productInfo && isDS && (
              <>
                <OpenTrialsButton
                  onClick={function () {
                    setIsOpen(true);
                    trackEvent('Button Clicked', {
                      buttonName: 'See Clinical Evidence',
                      location: 'PDP',
                    });
                  }}
                >
                  See Clinical Evidence
                  <Icon name='arrow-right' size='md' />
                </OpenTrialsButton>
                <ClinicalTrials
                  productInfo={productInfo}
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  contents={
                    richTextToHtml(clinicalTrials, { newLineToBreak: true }) ??
                    ''
                  }
                />
              </>
            )}
            {isDS && (
              <DSBenefitVideoMobileSlot>
                <DSProductBenefitImages
                  variant='video-only'
                  images={benefitImages ?? []}
                  videoUrl={benefits.videoUrl}
                  videoCaptionsUrl={benefits.videoCaptionsUrl}
                />
              </DSBenefitVideoMobileSlot>
            )}
            <BenefitsContents>
              <div>
                <ButtonWrapper ref={ref}>
                  {weeks.map((week, weekIndex) => (
                    <WeekItem
                      key={`week-benefits=${week.week}`}
                      className={currentWeek >= weekIndex ? 'selected' : ''}
                    >
                      <WeekTitle>
                        <WeekPill style={{ background: pillBackground }}>
                          {isDS
                            ? `${week.week} ${week.timeframe || 'Weeks'}`
                            : stepLabel(week.week)}
                        </WeekPill>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: supFootnoteMarkersHtml(
                              week?.overview || '',
                            ),
                          }}
                        ></span>
                      </WeekTitle>
                      {week.bullets && (
                        <WeekBenefits
                          className={`${week.bullets.length === 1 ? 'single-benefit' : ''}`}
                        >
                          {week.bullets.map((benefit, benefitIndex) => (
                            <li key={`benefit-${weekIndex}-${benefitIndex}`}>
                              {benefit}
                            </li>
                          ))}
                        </WeekBenefits>
                      )}
                    </WeekItem>
                  ))}
                </ButtonWrapper>
                <HowToUse>
                  {benefits.howToUseImage && (
                    <HowToUseImage
                      src={imageUrl(benefits.howToUseImage, 'medium')}
                      alt={benefits.howToUseImage.alt}
                    />
                  )}
                  <div>
                    <p>
                      <strong>How to Use:</strong>
                      {howToUseStega}
                    </p>
                    <HowToUseList>
                      {how_to_use_lines.map((how_to_use_line) => (
                        <li key={`how-to-use-${how_to_use_line}`}>
                          {how_to_use_line.trim()}.
                        </li>
                      ))}
                    </HowToUseList>
                  </div>
                </HowToUse>
              </div>
              {!isDS && <ProductBenefitImages images={benefitImages} />}
              {isDS && (
                <>
                  <DSDesktopBenefitMediaSlot>
                    <DSProductBenefitImages
                      variant='full'
                      images={benefitImages ?? []}
                      videoUrl={benefits.videoUrl}
                      videoCaptionsUrl={benefits.videoCaptionsUrl}
                    />
                  </DSDesktopBenefitMediaSlot>
                  <DSMobileBenefitImagesSlot>
                    <DSProductBenefitImages
                      variant='images-only'
                      images={benefitImages ?? []}
                      videoUrl={benefits.videoUrl}
                      videoCaptionsUrl={benefits.videoCaptionsUrl}
                    />
                  </DSMobileBenefitImagesSlot>
                </>
              )}
            </BenefitsContents>
          </>
        )}
      </BenefitsWrapper>
    );
  } catch (error) {
    console.error(error);
    return null;
  }
};

export default ProductBenefits;
